/**
 * The Overall view of the flow, as the marketing hero draws it, for any number of projects:
 * Projects, the agents at work (each with its own logo ring), one card of changes waiting to land,
 * and each project's own main branch, aligned with its project. Past four projects the main column
 * becomes a rollup ("1 red, 5 green"), problems first. A project whose main is red or held, or
 * with an agent that needs you, is never folded away.
 *
 * Lines join each project to its agents, every agent to the queue and each queued change to its
 * project's main; when work moves (a session starts working, a change is submitted, a change
 * lands) one dot travels its line. Nothing moves while the Overview is paused, the tab is hidden
 * or motion is reduced: the still twin is the same picture.
 */
import './flowGraph.css';
import { ChevronDown, CircleCheck, CircleX, Clock, GitBranch, Hand, Info, Pause, Plus } from '@orgus/ui/icons';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode, RefObject } from 'react';
import { useNavigate } from 'react-router';
import type { Id, Landing, Pipeline, Session } from '@orgus/protocol';
import { Button, ProjectMark, Pressable, RelativeTime, RowStatus, Spinner, Tooltip, cn, formatInt, plural } from '@orgus/ui';
import type { RowStatusProps } from '@orgus/ui';
import { useOrgus } from '../../lib/store.ts';
import { AgentGlyph, agentHue } from '../adapters/AgentKind.tsx';
import { openNewSession } from '../new-session/store.ts';
import { useSessionParam } from '../sessions/useSessionParam.ts';
import { STROKE, edgeAlpha } from './FlowGraph.tsx';
import type { FlowState } from './flow.ts';
import { buildHero, rollupText } from './hero.ts';
import type { HeroAgent, HeroMain, HeroModel, HeroQueueRow } from './hero.ts';
import { useHeroInput, useIncidents, useOverviewPause } from './useHero.ts';

interface Line {
  key: string;
  d: string;
  state: FlowState;
}

interface Dot {
  id: number;
  d: string;
  state: FlowState;
}

const DOTS_MAX = 8;
/** Up to this many projects, each main has its own node beside its project; past it, the rollup. */
const MAINS_INLINE = 4;
/** The rollup lists this many mains (problems first), then "+N green". */
const ROLLUP_ROWS = 5;
const reducedMotion = () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

const AGENT_EDGE: Record<HeroAgent['state'], FlowState> = { needs: 'attention', working: 'working', asked: 'idle', idle: 'idle' };
const MAIN_EDGE: Record<HeroMain['state'], FlowState> = { green: 'success', red: 'danger', held: 'attention', quiet: 'idle' };

/** Each main state as icon, colour and word, with what it means for a newcomer. */
export const MAIN_LOOK: Record<HeroMain['state'], { word: string; help: string; text: string; ring: string; Icon: typeof CircleCheck }> = {
  green: { word: 'Green', help: 'Its checks pass: changes keep landing.', text: 'text-status-success', ring: 'ring-status-success', Icon: CircleCheck },
  red: { word: 'Red', help: 'Its checks fail: landings wait until it is fixed.', text: 'text-status-danger', ring: 'ring-status-danger', Icon: CircleX },
  held: { word: 'Held', help: 'Landings are held: a pipeline waits before it lands more.', text: 'text-status-attention', ring: 'ring-status-attention', Icon: Pause },
  quiet: { word: 'Nothing landed', help: 'Nothing has landed through Orgus yet.', text: 'text-fg-subtle', ring: 'ring-border-strong', Icon: GitBranch },
};

const QUEUE_LOOK: Record<HeroQueueRow['state'], Pick<RowStatusProps, 'tone' | 'icon' | 'label'> & { help: string }> = {
  verifying: { tone: 'working', icon: 'working', label: 'Verifying', help: 'Its tests run on the rebased change now.' },
  queued: { tone: 'neutral', icon: 'queued', label: 'Waiting', help: 'Waiting for its turn to be verified.' },
  parked: { tone: 'attention', icon: 'needs-you', label: 'Needs you', help: 'Stopped on a conflict or a second failure: open it to fix.' },
};

const curve = (x1: number, y1: number, x2: number, y2: number) => {
  const mx = (x1 + x2) / 2;
  return `M ${x1} ${y1} C ${mx} ${y1} ${mx} ${y2} ${x2} ${y2}`;
};

/** The lines between the cards, measured from where they sit; redrawn when the box or the picture changes. */
function useLines(box: RefObject<HTMLDivElement | null>, els: RefObject<Map<string, HTMLElement>>, model: HeroModel, rollup: boolean, key: string): Line[] {
  const [lines, setLines] = useState<Line[]>([]);
  useLayoutEffect(() => {
    const root = box.current;
    if (!root) return;
    const draw = () => {
      const r = root.getBoundingClientRect();
      // Only what sits side by side: on a phone the columns stack and no line is drawn.
      if (getComputedStyle(root).display !== 'grid') return setLines([]);
      const at = (id: string) => {
        const b = els.current.get(id)?.getBoundingClientRect();
        return b && b.width > 0 ? { l: b.left - r.left, r: b.right - r.left, t: b.top - r.top, b: b.bottom - r.top, y: b.top - r.top + b.height / 2 } : null;
      };
      const out: Line[] = [];
      const queue = at('queue');
      model.agents.forEach((a, i) => {
        const card = at(`agent:${a.id}`);
        if (!card) return;
        const p = a.projectId ? at(`project:${a.projectId}`) : null;
        if (p) out.push({ key: `project:${a.projectId}>agent:${a.id}`, d: curve(p.r, p.y, card.l, card.y), state: AGENT_EDGE[a.state] });
        if (queue) {
          // Spread over the card's upper body, so the lines fan in like the hero's.
          const y = queue.t + 32 + ((queue.b - queue.t - 48) * (i + 0.5)) / Math.max(1, model.agents.length);
          out.push({ key: `agent:${a.id}>queue`, d: curve(card.r, card.y, queue.l, y), state: a.state === 'working' ? 'pending' : 'idle' });
        }
      });
      if (rollup) {
        const roll = at('mains');
        if (queue && roll) out.push({ key: 'queue>mains', d: curve(queue.r, queue.y, roll.l, roll.y), state: model.mains.red ? 'danger' : model.mains.held ? 'attention' : 'success' });
      } else
        for (const p of model.projects) {
          const main = at(`main:${p.id}`);
          if (!main) continue;
          // From this project's first change in the card, else from the card itself.
          const row = model.queue.rows.find((q) => q.projectId === p.id);
          const from = (row && at(`qrow:${row.id}`)) || queue;
          if (from) out.push({ key: `queue>main:${p.id}`, d: curve(from.r, from.y, main.l, main.y), state: MAIN_EDGE[p.main.state] });
        }
      setLines(out);
    };
    draw();
    const ro = new ResizeObserver(draw);
    ro.observe(root);
    return () => ro.disconnect();
  }, [key]);
  return lines;
}

/** A column's plain name, with a one-line explanation on hover or focus. */
function ColumnTitle({ children, help, className }: { children: string; help: string; className?: string }) {
  return (
    <h3 className={cn('flex pb-2 text-caption text-fg-subtle', className)}>
      <Tooltip content={help}>
        <span tabIndex={0} className="inline-flex cursor-help items-center gap-1 rounded-control">
          {children}
          <Info aria-hidden className="size-3 text-fg-faint" />
          <span className="sr-only">{`: ${help}`}</span>
        </span>
      </Tooltip>
    </h3>
  );
}

/** Between stacked columns on a phone: the flow reads top to bottom. */
const Down = () => (
  <span aria-hidden className="flex justify-center text-fg-faint md:hidden [&_svg]:size-4">
    <ChevronDown />
  </span>
);

function AgentRing({ a }: { a: HeroAgent }) {
  return (
    <span className={cn('relative flex size-8 shrink-0 items-center justify-center rounded-pill bg-surface ring-1 ring-current [&_svg]:size-4', agentHue(a.kind))}>
      {a.state === 'working' && <span aria-hidden className="motion-ripple absolute inset-0 rounded-pill border border-current" />}
      <AgentGlyph kind={a.kind} />
      {a.state === 'needs' && <span aria-hidden className="absolute -right-1 -top-1 flex size-3 items-center justify-center rounded-pill bg-status-attention-fill ring-2 ring-surface" />}
    </span>
  );
}

const CARD = 'cursor-pointer rounded-card border border-border bg-surface px-3 py-2 hover:border-border-strong hover:bg-hover';

function QueueRow({ r, rowRef, onOpen }: { r: HeroQueueRow; rowRef: (el: HTMLElement | null) => void; onOpen: () => void }) {
  const look = QUEUE_LOOK[r.state];
  return (
    <Tooltip content={`${r.projectName}: ${r.title}. ${look.help}`}>
      <Pressable ref={rowRef} onClick={onOpen} data-queue-row={r.state} className="flex min-w-0 cursor-pointer flex-col justify-center gap-1 rounded-control bg-hover px-2 py-1 hover:bg-active">
        <span className="flex min-w-0 items-center gap-2 text-caption">
          <ProjectMark projectId={r.projectId} name={r.projectName} size="xs" />
          <span className="min-w-0 flex-1 line-clamp-2 break-words font-medium text-fg num">{r.label}</span>
        </span>
        <span className="flex min-w-0 items-center gap-2">
          <RowStatus tone={look.tone} icon={look.icon} label={look.label} focusable={false} className="shrink-0 text-caption [&_svg]:size-3" />
          {r.state === 'verifying' && (
            <span aria-hidden className="block h-1 min-w-0 flex-1 overflow-hidden rounded-pill bg-active">
              <span className="block h-full origin-left rounded-pill bg-status-working-fill" style={{ transform: `scaleX(${r.progress ?? 0.5})`, opacity: r.progress == null ? 0.5 : 1 }} />
            </span>
          )}
        </span>
      </Pressable>
    </Tooltip>
  );
}

/** One project's main, as tall as its project card so they line up; on a phone it names its project. */
function MainNode({ m, nodeRef, onOpen }: { m: HeroMain; nodeRef: (el: HTMLElement | null) => void; onOpen: () => void }) {
  const look = MAIN_LOOK[m.state];
  const tip = [`${m.name} main: ${look.word}.`, m.why ?? look.help, m.last ? `Last landed: ${m.last}` : ''].filter(Boolean).join(' ');
  return (
    <Tooltip content={tip}>
      <Pressable ref={nodeRef} onClick={onOpen} data-main-state={m.state} className={cn(CARD, 'flex min-w-0 items-center gap-2')}>
        <span className={cn('flex size-6 shrink-0 items-center justify-center rounded-pill ring-1 [&_svg]:size-4', look.text, look.ring)}>
          <look.Icon aria-hidden />
        </span>
        <span className="flex min-w-0 flex-col">
          <span className={cn('truncate text-ui font-medium', m.state === 'quiet' ? 'text-fg-subtle' : look.text)}>
            <span className="md:hidden">{`${m.name}: `}</span>
            {look.word}
          </span>
          <span className="truncate text-caption text-fg-subtle">
            {m.sha ? <span className="font-mono">{m.sha}</span> : m.last ? 'Landed' : 'main'}
            {m.lastAt ? (
              <>
                {', '}
                <RelativeTime ts={m.lastAt} />
              </>
            ) : null}
          </span>
        </span>
      </Pressable>
    </Tooltip>
  );
}

/** Past four projects: every main at once, the red and held ones first and each one clickable. */
function MainRollup({ model, nodeRef, open }: { model: HeroModel; nodeRef: (el: HTMLElement | null) => void; open: (href: string) => void }) {
  const r = model.mains;
  const listed = r.mains.slice(0, ROLLUP_ROWS);
  const rest = r.mains.length - listed.length;
  const worst = r.red ? 'red' : r.held ? 'held' : r.green ? 'green' : 'quiet';
  const look = MAIN_LOOK[worst];
  return (
    <div ref={nodeRef} className="flex min-w-0 flex-col gap-2 rounded-card border border-border bg-surface p-3" data-main-rollup>
      <span className="flex min-w-0 items-center gap-2">
        <look.Icon aria-hidden className={cn('size-4 shrink-0', look.text)} />
        <span className="min-w-0 text-ui font-medium text-fg">{rollupText(r)}</span>
      </span>
      <ul className="flex flex-col gap-1">
        {listed.map((m) => {
          const l = MAIN_LOOK[m.state];
          return (
            <li key={m.projectId ?? m.href}>
              <Tooltip content={m.why ?? l.help}>
                <Pressable onClick={() => open(m.href)} className="flex min-w-0 cursor-pointer items-center gap-2 rounded-control px-1 py-1 text-caption hover:bg-hover">
                  <l.Icon aria-hidden className={cn('size-3.5 shrink-0', l.text)} />
                  <span className="min-w-0 flex-1 truncate text-fg-muted">{m.name}</span>
                  <span className={cn('shrink-0', l.text)}>{l.word}</span>
                </Pressable>
              </Tooltip>
            </li>
          );
        })}
      </ul>
      {rest > 0 && (
        <Pressable onClick={() => open('/pipelines/landing')} className="cursor-pointer px-1 text-left text-caption text-fg-subtle hover:text-fg-muted">
          {`+${formatInt(rest)} more`}
        </Pressable>
      )}
    </div>
  );
}

/** The states the picture uses, once, small: icon, colour and word. */
function Legend() {
  const items: Array<{ icon: ReactNode; word: string }> = [
    { icon: <Hand className="text-status-attention" />, word: 'Needs you' },
    { icon: <Spinner className="text-status-working" />, word: 'Working' },
    { icon: <Clock className="text-fg-subtle" />, word: 'Waiting' },
    { icon: <CircleCheck className="text-status-success" />, word: 'Green' },
    { icon: <CircleX className="text-status-danger" />, word: 'Red' },
  ];
  return (
    <ul aria-label="What the colours mean" className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-2 md:col-span-12">
      {items.map((i) => (
        <li key={i.word} className="flex items-center gap-1 text-caption text-fg-subtle [&_svg]:size-3">
          {i.icon}
          {i.word}
        </li>
      ))}
    </ul>
  );
}

/** Dots for what moved between two readings: a session starting work, a change submitted, a change landing. */
function moves(prev: { sessions: Record<Id, Session>; landing: Landing[]; pipelines: Pipeline[] }, next: typeof prev): { kind: 'start' | 'submit' | 'land'; sessionId?: Id; projectId?: Id }[] {
  const out: { kind: 'start' | 'submit' | 'land'; sessionId?: Id; projectId?: Id }[] = [];
  for (const s of Object.values(next.sessions)) if (s.status === 'working' && prev.sessions[s.id] && prev.sessions[s.id]!.status !== 'working') out.push({ kind: 'start', sessionId: s.id });
  const was = new Map(prev.landing.map((l) => [l.id, l.status]));
  for (const l of next.landing) {
    const before = was.get(l.id);
    if (before === l.status) continue;
    if (before === undefined && l.status === 'queued') out.push({ kind: 'submit', ...(l.sessionId ? { sessionId: l.sessionId } : {}), projectId: l.projectId });
    if (l.status === 'landed') out.push({ kind: 'land', projectId: l.projectId });
  }
  const seen = new Set(prev.pipelines.flatMap((p) => [...p.items, ...p.recent].map((i) => `${p.projectId}:${i.id}:${i.state}`)));
  for (const p of next.pipelines)
    for (const i of [...p.items, ...p.recent]) {
      if (seen.has(`${p.projectId}:${i.id}:${i.state}`)) continue;
      if (i.state === 'queued') out.push({ kind: 'submit', projectId: p.projectId });
      if (i.state === 'passed') out.push({ kind: 'land', projectId: p.projectId });
    }
  return out.slice(0, 6);
}

export function HeroFlow() {
  const navigate = useNavigate();
  const [, openSession] = useSessionParam();
  const { input, landingPhase } = useHeroInput();
  const incidents = useIncidents();
  const model = useMemo(() => buildHero(input, incidents), [input, incidents]);
  const rollup = model.moreProjects > 0 || model.projects.length > MAINS_INLINE || model.mains.mains.some((m) => m.projectId == null);
  const paused = useOverviewPause((s) => s.paused);
  const box = useRef<HTMLDivElement>(null);
  const els = useRef(new Map<string, HTMLElement>());
  const node = (id: string) => (el: HTMLElement | null) => {
    if (el) els.current.set(id, el);
    else els.current.delete(id);
  };
  const shape = `${rollup}|${model.projects.map((p) => `${p.id}:${p.main.state}`).join(',')}|${model.agents.map((a) => `${a.id}:${a.state}:${a.projectId}`).join(',')}|${model.queue.rows.map((r) => r.id).join(',')}|${model.mains.total}`;
  const lines = useLines(box, els, model, rollup, shape);

  // Motion: one dot along the line a change travelled.
  const sessions = useOrgus((s) => s.sessions);
  const [dots, setDots] = useState<Dot[]>([]);
  const seq = useRef(0);
  const latest = useRef({ lines, model });
  latest.current = { lines, model };
  const prev = useRef<{ sessions: Record<Id, Session>; landing: Landing[]; pipelines: Pipeline[] } | null>(null);
  useEffect(() => {
    const next = { sessions, landing: input.landing?.landings ?? [], pipelines: input.pipelines as Pipeline[] };
    const before = prev.current;
    prev.current = next;
    if (!before || paused || reducedMotion() || (typeof document !== 'undefined' && document.visibilityState === 'hidden')) return;
    const { lines: ls, model: m } = latest.current;
    const add: Dot[] = [];
    for (const mv of moves(before, next)) {
      let line: Line | undefined;
      if (mv.kind === 'land') line = ls.find((l) => l.key === `queue>main:${mv.projectId}`) ?? ls.find((l) => l.key === 'queue>mains');
      else if (mv.kind === 'start') line = ls.find((l) => l.key.endsWith(`>agent:${mv.sessionId}`));
      else {
        const agent = m.agents.find((a) => a.id === mv.sessionId) ?? m.agents.find((a) => a.projectId === mv.projectId);
        line = agent ? ls.find((l) => l.key === `agent:${agent.id}>queue`) : undefined;
      }
      if (line) add.push({ id: ++seq.current, d: line.d, state: line.state });
    }
    if (add.length) setDots((all) => [...all, ...add].slice(-DOTS_MAX));
  }, [sessions, input.landing, input.pipelines]);
  useEffect(() => {
    if (paused) setDots([]);
  }, [paused]);

  if (!model.projects.length && !model.agents.length && !model.queue.total && !model.mains.total)
    return (
      <div className="flex flex-col items-center gap-3 py-12 text-center">
        <p className="text-ui text-fg">{landingPhase === 'loading' ? 'Reading your projects' : 'No work in progress yet'}</p>
        <p className="max-w-md text-caption text-fg-subtle">Add a project and start a session: its agent, the changes waiting to land and the project's main branch appear here, joined as work moves.</p>
        <span className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={() => navigate('/projects')}>
            Add a project
          </Button>
          <Button size="sm" icon={<Plus />} onClick={() => openNewSession()}>
            New session
          </Button>
        </span>
      </div>
    );

  return (
    <div ref={box} className="relative flex min-w-0 flex-col gap-3 md:grid md:grid-cols-12 md:gap-x-8 md:gap-y-0" data-hero-flow>
      <svg aria-hidden className="pointer-events-none absolute inset-0 z-(--z-base) hidden size-full overflow-visible md:block">
        {lines.map((l) => {
          const s = STROKE[l.state];
          return <path key={l.key} d={l.d} fill="none" stroke={s.color} style={{ strokeOpacity: edgeAlpha(s.opacity) }} strokeWidth={s.dashed ? 1 : 1.5} strokeDasharray={s.dashed ? '3 4' : undefined} />;
        })}
      </svg>
      {dots.map((d) => (
        <span
          key={d.id}
          aria-hidden
          className="motion-flow orgus-overview-flow-dot pointer-events-none absolute left-0 top-0 z-(--z-sticky) hidden size-2 rounded-pill md:block"
          style={{ offsetPath: `path('${d.d}')`, color: d.state === 'idle' || d.state === 'pending' ? 'var(--live-trace-strong)' : STROKE[d.state].color }}
          onAnimationEnd={() => setDots((all) => all.filter((x) => x.id !== d.id))}
        />
      ))}

      <section aria-label="Projects" className="relative z-(--z-base) flex min-w-0 flex-col md:col-span-3">
        <ColumnTitle help="Repositories with work in progress.">Projects</ColumnTitle>
        <div className="flex flex-1 flex-col justify-center gap-2">
          {model.projects.map((p) => (
            <Pressable key={p.id} ref={node(`project:${p.id}`)} onClick={() => navigate(`/projects?project=${encodeURIComponent(p.id)}`)} className={cn(CARD, 'flex min-w-0 items-center gap-2')}>
              <ProjectMark projectId={p.id} name={p.name} />
              <span className="flex min-w-0 flex-col">
                <span className="truncate text-ui font-medium text-fg">{p.name}</span>
                <span className={cn('truncate text-caption num', p.needs ? 'text-status-attention' : 'text-fg-subtle')}>{p.needs ? `${formatInt(p.needs)} need you` : p.sessions ? plural(p.sessions, 'session') : 'No live session'}</span>
              </span>
            </Pressable>
          ))}
          {model.moreProjects > 0 && (
            <Pressable onClick={() => navigate('/projects')} className="cursor-pointer px-3 text-left text-caption text-fg-subtle hover:text-fg-muted">{`+${formatInt(model.moreProjects)} more projects`}</Pressable>
          )}
        </div>
      </section>
      <Down />

      <section aria-label="Agents at work" className="relative z-(--z-base) flex min-w-0 flex-col md:col-span-3">
        <ColumnTitle help="Each live session: its agent, and what it is doing or that it needs you.">Agents at work</ColumnTitle>
        <div className="flex flex-1 flex-col justify-center gap-2">
          {model.agents.map((a) => (
            <Tooltip key={a.id} content={`${a.name}, ${a.state === 'needs' ? 'needs you' : a.state}: ${a.task}`}>
              <Pressable ref={node(`agent:${a.id}`)} onClick={() => openSession(a.id)} className={cn(CARD, 'flex min-w-0 items-center gap-3')} data-agent-state={a.state}>
                <AgentRing a={a} />
                <span className="flex min-w-0 flex-col">
                  <span className="truncate text-ui font-medium text-fg">{a.name}</span>
                  {a.state === 'needs' ? (
                    <span className="flex min-w-0 items-center gap-1 text-caption font-medium text-status-attention">
                      <Hand aria-hidden className="size-3 shrink-0" />
                      Needs you
                    </span>
                  ) : (
                    <span className="truncate text-caption text-fg-subtle">{a.task}</span>
                  )}
                </span>
              </Pressable>
            </Tooltip>
          ))}
          {model.moreAgents > 0 && (
            <Pressable onClick={() => navigate('/sessions')} className="cursor-pointer px-3 text-left text-caption text-fg-subtle hover:text-fg-muted">{`+${formatInt(model.moreAgents)} more sessions`}</Pressable>
          )}
          {!model.agents.length && (
            <span className="flex flex-col items-start gap-2 text-caption text-fg-subtle">
              No agent at work
              <Button variant="ghost" size="sm" icon={<Plus />} onClick={() => openNewSession()}>
                New session
              </Button>
            </span>
          )}
          {model.external > 0 && (
            <Pressable onClick={() => navigate('/sessions')} className={`cursor-pointer text-left text-caption text-fg-subtle hover:text-fg-muted ${model.agents.length ? 'px-3' : ''}`}>{`${formatInt(model.external)} external ${model.external === 1 ? 'session' : 'sessions'} outside your projects`}</Pressable>
          )}
        </div>
      </section>
      <Down />

      <section aria-label="Waiting to land" className="relative z-(--z-base) flex min-w-0 flex-col md:col-span-3">
        <ColumnTitle help="Changes verified one at a time before they land on main.">Waiting to land</ColumnTitle>
        <div className="flex flex-1 flex-col justify-center">
          <div ref={node('queue')} className="flex min-w-0 flex-col gap-2 rounded-card border border-border bg-surface p-3">
            <Pressable onClick={() => navigate('/pipelines/landing')} className="flex min-w-0 cursor-pointer flex-col rounded-control text-left hover:text-fg">
              <span className="text-ui font-medium text-fg">{model.queue.total ? plural(model.queue.total, 'change') : 'Landing queue'}</span>
              <span className="text-caption text-fg-subtle">Rebase, verify, land</span>
            </Pressable>
            {model.queue.rows.map((r) => (
              <QueueRow key={r.id} r={r} rowRef={node(`qrow:${r.id}`)} onOpen={() => navigate(r.href)} />
            ))}
            {model.queue.more > 0 && (
              <Pressable onClick={() => navigate('/pipelines/landing')} className="cursor-pointer px-2 text-left text-caption text-fg-subtle hover:text-fg-muted">{`+${formatInt(model.queue.more)} more`}</Pressable>
            )}
            {!model.queue.total && <span className="px-2 text-caption text-fg-subtle">{landingPhase === 'loading' ? 'Reading the queue' : 'Nothing waiting to land'}</span>}
          </div>
        </div>
      </section>
      <Down />

      <section aria-label="Main branch" className="relative z-(--z-base) flex min-w-0 flex-col md:col-span-3">
        <ColumnTitle help="Each project's main branch: green when its checks pass, red when they fail, held when landings wait.">Main branch</ColumnTitle>
        <div className="flex flex-1 flex-col justify-center gap-2">
          {rollup ? (
            <MainRollup model={model} nodeRef={node('mains')} open={(href) => navigate(href)} />
          ) : (
            model.projects.map((p) => <MainNode key={p.id} m={p.main} nodeRef={node(`main:${p.id}`)} onOpen={() => navigate(p.main.href)} />)
          )}
        </div>
      </section>
      <Legend />
    </div>
  );
}
